/**
 * FrostedFanModal — Apple Home 風格毛玻璃 Fan 控制面板
 *
 * 設計語言：
 * - Slate/Cool Gray tint → Fan 設備專用色調
 * - Spinning fan icon dial → 旋轉風扇 + 速度文字
 * - Mode: Normal / Natural / Sleep
 * - Speed: 5 levels (1-5)
 * - Oscillation toggle row
 * - Timer presets (Off/1H/2H/4H/8H)
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { executeCommandWithAuth, configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import {
  resolveModesFromAttributes,
  resolveFanSpeedLabelsFromCustomControls,
  resolveCustomModesFromMcp,
  resolveCustomModeIcon,
  resolveCurrentCustomModeValue,
  resolveCustomTogglesFromAttributes,
  FAN_SPEED_DISPLAY_LABELS,
  type CustomModeResolvedConfig,
} from '../components/traitStyles/traitStyleConfig'
import './FrostedFanModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const FAN_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'dial', label: 'Fan Speed Dial', icon: 'mode_fan', slots: [
    { id: 'dial-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'dial-icon', label: 'Fan Icon', editableProps: ['fontSize', 'color'] },
    { id: 'dial-label', label: 'Speed Label', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'mode', label: 'Mode', icon: 'air', slots: [
    { id: 'mode-bar', label: 'Mode Bar Container', editableProps: ['background', 'borderRadius', 'border'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'fontSize'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'speed', label: 'Fan Speed', icon: 'speed', slots: [
    { id: 'speed-label', label: 'Section Label', editableProps: ['fontSize', 'color', 'letterSpacing'] },
    { id: 'speed-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'border'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  { id: 'oscillation', label: 'Oscillation', icon: 'sync', slots: [] },
  { id: 'toggles', label: 'Toggles', icon: 'toggle_on', slots: [] },
  { id: 'timer', label: 'Timer', icon: 'timer', slots: [] },
]

const DEFAULT_SECTION_ORDER = FAN_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIMER_PRESETS = [
  { id: 'off', label: 'Off', minutes: 0 },
  { id: '1h', label: '1H', minutes: 60 },
  { id: '2h', label: '2H', minutes: 120 },
  { id: '4h', label: '4H', minutes: 240 },
  { id: '8h', label: '8H', minutes: 480 },
] as const

/** Shorten timer labels for compact button display */
function shortenTimerLabel(label: string): string {
  const l = label.toLowerCase()
  if (l.includes('no timer') || l.includes('no ') || l === 'off') return 'Off'
  // "Turn off after 1 hr" → "1H", "Turn off after 2 hours" → "2H"
  const match = l.match(/(\d+)\s*(?:hr|hour|h)/)
  if (match) return `${match[1]}H`
  // "30 min" → "30M"
  const minMatch = l.match(/(\d+)\s*(?:min|m)/)
  if (minMatch) return `${minMatch[1]}M`
  // Fallback: truncate
  return label.length > 6 ? label.slice(0, 5) + '…' : label
}

/** Extract endpoint from device SN (e.g. "UTU500-xxx#3" → "3") */
function getEndpoint(sn: string): string | undefined {
  const idx = sn.indexOf('#')
  return idx >= 0 ? sn.slice(idx + 1) : undefined
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// Ring dial — uses circle + stroke-dasharray
const RING_R = 85
const RING_CX = 100
const RING_CY = 100
const RING_CIRC = 2 * Math.PI * RING_R // ~534
const RING_ARC_RATIO = 0.75 // 270° out of 360°
const RING_TRACK_OFFSET = RING_CIRC * (1 - RING_ARC_RATIO)

// Speed index → dash offset (dynamic based on total count)
function speedToDashOffset(index: number, total: number): number {
  const maxIdx = Math.max(1, total - 1)
  const ratio = Math.max(0, Math.min(1, index / maxIdx))
  const arcLen = RING_CIRC * RING_ARC_RATIO
  return RING_CIRC - ratio * arcLen
}

const DEG = Math.PI / 180
function speedToDotPos(index: number, total: number): { x: number; y: number } {
  const maxIdx = Math.max(1, total - 1)
  const ratio = Math.max(0, Math.min(1, index / maxIdx))
  const angleDeg = 135 + ratio * 270
  const rad = angleDeg * DEG
  return {
    x: RING_CX + RING_R * Math.cos(rad),
    y: RING_CY + RING_R * Math.sin(rad),
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedFanModal({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- Resolve capabilities from device ----
  const endpoint = useMemo(() => getEndpoint(device.sn), [device.sn])
  // Resolve attributes: try top-level, then capabilities.attributes, then capabilities[].attributes
  const rawDevice = device as Record<string, unknown>
  const attrs = useMemo(() => {
    const topAttrs = rawDevice.attributes as Record<string, unknown> | undefined
    if (topAttrs?.availableCustomModes || topAttrs?.availableCustomToggles) return topAttrs
    const caps = rawDevice.capabilities
    if (Array.isArray(caps)) {
      for (const cap of caps) {
        const a = (cap as Record<string, unknown>)?.attributes as Record<string, unknown> | undefined
        if (a?.availableCustomModes || a?.availableCustomToggles) return a
      }
    } else if (caps && typeof caps === 'object') {
      const a = (caps as Record<string, unknown>).attributes as Record<string, unknown> | undefined
      if (a?.availableCustomModes || a?.availableCustomToggles) return a
    }
    return topAttrs
  }, [rawDevice.attributes, rawDevice.capabilities])
  const customCtrl = rawDevice.customControls as Record<string, unknown> | undefined
  const traits = device.traits as string[] | undefined
  const hasTrait = useCallback((t: string) => {
    if (!traits) return false
    return traits.some(tr => tr.toLowerCase() === t.toLowerCase())
  }, [traits])

  // ---- Fan Speed resolution ----
  // Priority: 1) FanSpeed trait → use SetFanSpeed command
  //           2) CustomModes with modeName containing "fan"/"speed" → use SetModes command
  //           3) fallback ['low','medium','high','auto']
  const hasFanSpeedTrait = hasTrait('FanSpeed')

  // Check if CustomModes has a FanSpeed-like mode
  const rawCustomModes = attrs?.availableCustomModes as Array<{ name?: string; settings?: string[] }> | undefined
  // eslint-disable-next-line no-console
  if (device.sn?.includes('FAN')) console.log('[FAN-CAP]', { sn: device.sn, hasAttrs: !!attrs, attrsKeys: attrs ? Object.keys(attrs) : null, rawCustomModes, capsType: typeof rawDevice.capabilities, capsIsArr: Array.isArray(rawDevice.capabilities), topAttrsKeys: rawDevice.attributes ? Object.keys(rawDevice.attributes as Record<string, unknown>) : null })
  const fanSpeedCustomMode = useMemo(() => {
    if (!Array.isArray(rawCustomModes)) return null
    return rawCustomModes.find(m => {
      const name = String(m.name || '').toLowerCase()
      return name === 'fanspeed' || name === 'fan_speed' || name === 'fan speed'
    }) || null
  }, [rawCustomModes])

  // Resolve speed IDs and labels
  const { fanSpeedIds, fanSpeedLabels, fanSpeedCommand } = useMemo(() => {
    if (hasFanSpeedTrait) {
      // Real FanSpeed trait → use SetFanSpeed
      const ids = resolveModesFromAttributes(attrs, 'FanSpeed')
      const fromCustom = resolveFanSpeedLabelsFromCustomControls(customCtrl, endpoint, ids, 'en')
      return { fanSpeedIds: ids, fanSpeedLabels: { ...FAN_SPEED_DISPLAY_LABELS, ...fromCustom }, fanSpeedCommand: 'SetFanSpeed' as const }
    }
    if (fanSpeedCustomMode) {
      // CustomModes FanSpeed → use SetModes
      const ids = Array.isArray(fanSpeedCustomMode.settings) ? fanSpeedCustomMode.settings : []
      const epKey = endpoint || 'FAN'
      const epData = (customCtrl?.[epKey] ?? customCtrl) as Record<string, unknown> | undefined
      const modes = (epData?.Modes ?? epData?.modes) as Record<string, unknown> | undefined
      const fanMode = (modes?.FanSpeed ?? modes?.fanSpeed) as Record<string, unknown> | undefined
      // Try en first, then zh-TW
      const enData = (fanMode?.en ?? fanMode?.['en']) as Record<string, unknown> | undefined
      const zhData = (fanMode?.['zh-TW']) as Record<string, unknown> | undefined
      const settingSynonym = (enData?.SettingSynonym ?? zhData?.SettingSynonym ?? {}) as Record<string, unknown>
      const labels: Record<string, string> = {}
      for (const id of ids) {
        const syn = settingSynonym[id]
        labels[id] = Array.isArray(syn) ? String(syn[0]) : String(syn || id)
      }
      return { fanSpeedIds: ids, fanSpeedLabels: labels, fanSpeedCommand: 'SetModes' as const }
    }
    // Fallback
    return {
      fanSpeedIds: ['low', 'medium', 'high', 'auto'],
      fanSpeedLabels: { ...FAN_SPEED_DISPLAY_LABELS },
      fanSpeedCommand: 'SetFanSpeed' as const,
    }
  }, [hasFanSpeedTrait, fanSpeedCustomMode, attrs, customCtrl, endpoint])

  // ---- Non-FanSpeed CustomModes → split into regular modes vs timer modes ----
  const { regularModes, timerMode } = useMemo(() => {
    const empty = { modeName: 'Mode', options: [] } as CustomModeResolvedConfig
    if (!Array.isArray(rawCustomModes)) return { regularModes: empty, timerMode: null as CustomModeResolvedConfig | null }

    const nonFanSpeed = rawCustomModes.filter(m => {
      const name = String(m.name || '').toLowerCase()
      return name !== 'fanspeed' && name !== 'fan_speed' && name !== 'fan speed'
    })
    if (nonFanSpeed.length === 0) return { regularModes: empty, timerMode: null }

    // Detect timer-like modes by name or settings
    const isTimerLike = (m: { name?: string; settings?: string[] }) => {
      const name = String(m.name || '').toLowerCase()
      if (name.includes('timer') || name.includes('delay') || name.includes('schedule')) return true
      const settings = Array.isArray(m.settings) ? m.settings : []
      return settings.some(s => {
        const sl = String(s).toLowerCase()
        return sl.includes('timer') || sl.includes('turn off') || sl.includes('hour') || sl.includes(' hr')
          || sl.includes('no timer') || sl.includes('delay')
      })
    }

    const timerModes = nonFanSpeed.filter(isTimerLike)
    const regularModesArr = nonFanSpeed.filter(m => !isTimerLike(m))

    const resolvedRegular = regularModesArr.length > 0
      ? resolveCustomModesFromMcp({ ...attrs, availableCustomModes: regularModesArr }, customCtrl, endpoint, 'en', traits)
      : empty

    const resolvedTimer = timerModes.length > 0
      ? resolveCustomModesFromMcp({ ...attrs, availableCustomModes: timerModes }, customCtrl, endpoint, 'en', traits)
      : null

    return { regularModes: resolvedRegular, timerMode: resolvedTimer }
  }, [rawCustomModes, attrs, customCtrl, endpoint, traits])
  const hasRegularModes = regularModes.options.length > 0
  const hasTimerMode = timerMode !== null && timerMode.options.length > 0

  // ---- CustomToggles (Light, Buzzer, etc.) ----
  const customToggleIds = useMemo(() => {
    if (!hasTrait('CustomToggles')) return []
    const toggles = attrs?.availableCustomToggles
    return Array.isArray(toggles) ? toggles.map(String) : []
  }, [hasTrait, attrs])
  const hasCustomToggles = customToggleIds.length > 0

  // Resolve toggle labels from customControls
  const toggleLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    if (!customCtrl) return labels
    const epKey = endpoint || 'FAN'
    const epData = (customCtrl[epKey] ?? customCtrl) as Record<string, unknown> | undefined
    const toggles = (epData?.Toggles ?? epData?.toggles) as Record<string, unknown> | undefined
    if (!toggles) return labels
    for (const id of customToggleIds) {
      const toggle = toggles[id] as Record<string, unknown> | undefined
      const enData = (toggle?.en ?? toggle?.['en']) as Record<string, unknown> | undefined
      const zhData = (toggle?.['zh-TW']) as Record<string, unknown> | undefined
      const nameSyn = (enData?.NameSynonym ?? zhData?.NameSynonym) as string[] | undefined
      labels[id] = Array.isArray(nameSyn) && nameSyn[0] ? nameSyn[0] : id
    }
    return labels
  }, [customCtrl, endpoint, customToggleIds])

  // ---- Trait flags ----
  const hasRotation = hasTrait('Rotation')

  // ---- State from device ----
  const isOn = Boolean(getAttr(deviceState, 'on'))
  // FanSpeed state: check both currentFanSpeedSetting and currentModeSettings.FanSpeed
  const currentFanSpeed = useMemo(() => {
    if (hasFanSpeedTrait) return strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || fanSpeedIds[0] || 'low'
    // CustomModes FanSpeed → read from currentModeSettings
    const modeSettings = (deviceState as Record<string, unknown>)?.currentModeSettings as Record<string, unknown> | undefined
    const val = modeSettings?.FanSpeed ?? modeSettings?.fanSpeed
    return val != null ? String(val) : fanSpeedIds[0] || '0'
  }, [hasFanSpeedTrait, deviceState, fanSpeedIds])
  const currentRotation = Boolean(getAttr(deviceState, 'rotationPercent', 'rotation', 'oscillation'))
  const currentModeValue = useMemo(
    () => hasRegularModes
      ? resolveCurrentCustomModeValue(deviceState as Record<string, unknown> | undefined, regularModes.modeName, regularModes.options[0]?.modeValue || '')
      : '',
    [deviceState, regularModes, hasRegularModes]
  )
  // Toggle states
  const toggleStates = useMemo(() => {
    const states: Record<string, boolean> = {}
    const toggleSettings = (deviceState as Record<string, unknown>)?.currentToggleSettings as Record<string, unknown> | undefined
    for (const id of customToggleIds) {
      states[id] = Boolean(toggleSettings?.[id])
    }
    return states
  }, [deviceState, customToggleIds])

  // ---- Settings page ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    if (device.uplink_ssid) items.push({ label: 'UPLINK SSID', value: String(device.uplink_ssid) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label)
    setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])

  const startRename = useCallback(() => {
    setRenameValue(displayName)
    setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])

  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) {
      setIsRenaming(false)
      return
    }
    setDisplayName(trimmed)
    setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Local UI state (optimistic updates) ----
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const [pendingSpeed, setPendingSpeed] = useState<string | null>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingRotation, setPendingRotation] = useState<boolean | null>(null)
  const [selectedTimer, setSelectedTimer] = useState('off')

  const [pendingToggles, setPendingToggles] = useState<Record<string, boolean>>({})

  const displayPower = pendingPower ?? isOn
  const displaySpeed = pendingSpeed ?? currentFanSpeed
  const displayRotation = pendingRotation ?? currentRotation
  const displayMode = pendingMode ?? currentModeValue

  // Sync pending states
  useEffect(() => { if (pendingPower !== null && pendingPower === isOn) setPendingPower(null) }, [isOn, pendingPower])
  useEffect(() => { if (pendingSpeed !== null && pendingSpeed === currentFanSpeed) setPendingSpeed(null) }, [currentFanSpeed, pendingSpeed])
  useEffect(() => { if (pendingRotation !== null && pendingRotation === currentRotation) setPendingRotation(null) }, [currentRotation, pendingRotation])

  // ---- Internal API caller ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setSpeedCmd = useCallback((speedValue: string) => {
    if (isEditorMode) return
    setPendingSpeed(speedValue)
    if (fanSpeedCommand === 'SetFanSpeed') {
      execCmd('SetFanSpeed', { fanSpeed: speedValue })
    } else {
      // CustomModes FanSpeed → SetModes
      execCmd('SetModes', { updateModeSettings: { FanSpeed: speedValue } })
    }
  }, [isEditorMode, execCmd, fanSpeedCommand])

  const setModeCmd = useCallback((modeValue: string) => {
    if (isEditorMode) return
    setPendingMode(modeValue)
    execCmd('SetModes', { updateModeSettings: { [regularModes.modeName]: modeValue } })
  }, [isEditorMode, execCmd, regularModes.modeName])

  const setToggleCmd = useCallback((toggleId: string, value: boolean) => {
    if (isEditorMode) return
    setPendingToggles(prev => ({ ...prev, [toggleId]: value }))
    execCmd('SetToggles', { updateToggleSettings: { [toggleId]: value } })
  }, [isEditorMode, execCmd])

  const toggleOscillation = useCallback(() => {
    if (isEditorMode) return
    const next = !displayRotation
    setPendingRotation(next)
    execCmd('setRotation', { rotationPercent: next ? 100 : 0 })
  }, [isEditorMode, displayRotation, execCmd])

  // ---- Ring dial values (dynamic based on actual speed count) ----
  const speedIndex = fanSpeedIds.indexOf(displaySpeed)
  const safeIndex = speedIndex >= 0 ? speedIndex : 0
  const valueDashOffset = speedToDashOffset(safeIndex, fanSpeedIds.length)
  const dotPos = speedToDotPos(safeIndex, fanSpeedIds.length)

  const speedLabel = fanSpeedLabels[displaySpeed] || displaySpeed

  const stateClass = displayPower ? 'fan-frosted--on' : 'fan-frosted--off'
  const hl = (id: string) => highlightedSection === id ? ' section-highlighted' : ''

  // ---- Render sections ----
  const renderSection = (sectionId: string) => {
    if (!isSectionVisible(sectionId)) return null

    switch (sectionId) {
      case 'dial':
        return (
          <div key="dial" className={`fan-frosted__dial${hl('dial')}`} data-modal-section="dial">
            <svg viewBox="0 0 200 200" className="fan-frosted__ring-svg">
              <defs>
                <linearGradient id="fanfSlateGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#94a3b8"/>
                  <stop offset="100%" stopColor="#cbd5e1"/>
                </linearGradient>
              </defs>
              {/* Track (270° arc) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={RING_TRACK_OFFSET}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
              />
              {/* Value arc (slate) */}
              <circle
                cx={RING_CX} cy={RING_CY} r={RING_R}
                fill="none" stroke="url(#fanfSlateGrad)" strokeWidth={16}
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={displayPower ? valueDashOffset : RING_CIRC}
                transform={`rotate(135,${RING_CX},${RING_CY})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
              {/* Dot indicator */}
              {displayPower && (
                <circle
                  cx={dotPos.x} cy={dotPos.y} r={7}
                  fill="#fff"
                  style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}
                />
              )}
            </svg>
            <div className="fan-frosted__dial-text">
              {displayPower ? (
                <>
                  <span className="fan-frosted__fan-icon material-symbols-outlined">mode_fan</span>
                  <span className="fan-frosted__speed-label-center">Level {speedLabel}</span>
                </>
              ) : (
                <span className="fan-frosted__off-text">OFF</span>
              )}
            </div>
            {/* Center tap for power toggle */}
            <button
              className="fan-frosted__power-tap"
              onClick={togglePower}
              aria-label={displayPower ? 'Turn off' : 'Turn on'}
            />
          </div>
        )

      case 'mode':
        // Only show if device has non-FanSpeed CustomModes
        if (!hasRegularModes) return null
        return (
          <div key="mode" className={`fan-frosted__modes${hl('mode')}`} data-modal-section="mode">
            {regularModes.options.map(m => (
              <button
                key={m.id}
                className={`fan-frosted__mode-btn${displayMode === m.modeValue ? ' active' : ''}`}
                onClick={() => setModeCmd(m.modeValue)}
              >
                <span className="icon material-symbols-outlined" style={{ fontSize: 16 }}>{resolveCustomModeIcon(m.modeValue)}</span> {m.label}
              </button>
            ))}
          </div>
        )

      case 'speed':
        return (
          <div key="speed" className={`fan-frosted__speed${hl('speed')}`} data-modal-section="speed">
            <div className="fan-frosted__speed-title">Fan Speed</div>
            <div className="fan-frosted__speed-buttons">
              {fanSpeedIds.map(speedId => (
                <button
                  key={speedId}
                  className={`fan-frosted__speed-btn${displaySpeed === speedId ? ' active' : ''}`}
                  onClick={() => setSpeedCmd(speedId)}
                >
                  {fanSpeedLabels[speedId] || speedId}
                </button>
              ))}
            </div>
          </div>
        )

      case 'oscillation':
        if (!hasRotation) return null
        return (
          <div key="oscillation" className={`fan-frosted__oscillation${hl('oscillation')}`} data-modal-section="oscillation">
            <div className="fan-frosted__osc-left">
              <span className="fan-frosted__osc-icon material-symbols-outlined">sync</span>
              <div>
                <div className="fan-frosted__osc-label">Oscillation</div>
                <div className="fan-frosted__osc-sub">Left-right sweep</div>
              </div>
            </div>
            <button
              className={`fan-frosted__toggle${displayRotation ? ' fan-frosted__toggle--on' : ''}`}
              onClick={toggleOscillation}
              aria-label="Toggle oscillation"
            >
              <div className="fan-frosted__toggle-knob" />
            </button>
          </div>
        )

      case 'toggles':
        if (!hasCustomToggles) return null
        return (
          <div key="toggles" data-modal-section="toggles">
            {customToggleIds.map(toggleId => {
              const isActive = pendingToggles[toggleId] ?? toggleStates[toggleId] ?? false
              return (
                <div key={toggleId} className="fan-frosted__oscillation" style={{ marginBottom: 8 }}>
                  <div className="fan-frosted__osc-left">
                    <span className="fan-frosted__osc-icon material-symbols-outlined">
                      {toggleId.toLowerCase() === 'light' ? 'light' : toggleId.toLowerCase() === 'buzzer' ? 'volume_up' : 'toggle_on'}
                    </span>
                    <div>
                      <div className="fan-frosted__osc-label">{toggleLabels[toggleId] || toggleId}</div>
                    </div>
                  </div>
                  <button
                    className={`fan-frosted__toggle${isActive ? ' fan-frosted__toggle--on' : ''}`}
                    onClick={() => setToggleCmd(toggleId, !isActive)}
                    aria-label={`Toggle ${toggleId}`}
                  >
                    <div className="fan-frosted__toggle-knob" />
                  </button>
                </div>
              )
            })}
          </div>
        )

      case 'timer':
        if (!hasTimerMode || !timerMode) return null
        return (
          <div key="timer" className={`fan-frosted__timer${hl('timer')}`} data-modal-section="timer">
            <div className="fan-frosted__timer-header">
              <div className="fan-frosted__timer-title">
                <span className="material-symbols-outlined">timer</span> Timer
              </div>
            </div>
            <div className="fan-frosted__timer-buttons">
              {timerMode.options.map(t => {
                const isActive = (pendingMode ?? resolveCurrentCustomModeValue(
                  deviceState as Record<string, unknown> | undefined,
                  timerMode.modeName,
                  timerMode.options[0]?.modeValue || ''
                )) === t.modeValue
                // Shorten label for compact display
                const shortLabel = shortenTimerLabel(t.label)
                return (
                  <button
                    key={t.id}
                    className={`fan-frosted__timer-btn${isActive ? ' active' : ''}`}
                    onClick={() => {
                      if (isEditorMode) return
                      execCmd('SetModes', { updateModeSettings: { [timerMode.modeName]: t.modeValue } })
                    }}
                  >
                    {shortLabel}
                  </button>
                )
              })}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={`fan-frosted ${stateClass}`}
      data-modal-template-id="fan-frosted"
      onContextMenu={handleContextMenu}
    >
      {cssOverrides && <style>{cssOverrides}</style>}

      {/* Header */}
      <div className="fan-frosted__header" data-modal-section="_modal">
        <button className="fan-frosted__back" onClick={onClose} aria-label="Back">&lsaquo;</button>
        <span className="fan-frosted__name">{displayName}</span>
        <button
          className={`fan-frosted__power-btn${displayPower ? ' fan-frosted__power-btn--on' : ''}`}
          onClick={togglePower}
          aria-label="Power"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 20 }}>power_settings_new</span>
        </button>
      </div>

      {/* Ordered sections */}
      {orderedSections.map(id => renderSection(id))}

      {/* Settings button */}
      <button
        className="fan-frosted__settings-btn"
        onClick={() => setShowSettings(true)}
        aria-label="Settings"
      >
        <span className="material-symbols-outlined" style={{ fontSize: 28 }}>settings</span>
      </button>

      {/* Settings overlay */}
      {showSettings && (
        <div className="fan-frosted__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="fan-frosted__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="fan-frosted__settings-header">
              <span className="fan-frosted__settings-title">Settings</span>
              <button className="fan-frosted__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            {/* Rename */}
            <div className="fan-frosted__settings-section">
              <div className="fan-frosted__settings-section-title">Device Name</div>
              {isRenaming ? (
                <div className="fan-frosted__settings-row">
                  <input
                    ref={renameInputRef}
                    className="fan-frosted__rename-input"
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={submitRename}
                    maxLength={40}
                  />
                </div>
              ) : (
                <div className="fan-frosted__settings-row">
                  <span className="fan-frosted__settings-value" style={{ textAlign: 'left', flex: 1 }}>
                    {displayName}
                  </span>
                  <button className="fan-frosted__settings-edit" onClick={startRename} title="Rename">
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                  </button>
                </div>
              )}
            </div>

            {/* Device Info */}
            <div className="fan-frosted__settings-section">
              <div className="fan-frosted__settings-section-title">Device Info</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="fan-frosted__settings-row">
                  <span className="fan-frosted__settings-label">{item.label}</span>
                  <span className="fan-frosted__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button
                      className="fan-frosted__settings-copy"
                      onClick={() => copyToClipboard(item.value, item.label)}
                      title="Copy"
                    >
                      📋
                      {copiedField === item.label && (
                        <span className="fan-frosted__copied-toast">Copied</span>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
